import styled from '@emotion/styled';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Search, { SearchInput, SearchList } from 'aquilla/core/molecules/Search';
import { Spacings } from '@styles/index';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import useRecentSearches from '@api/hooks/search/useRecentSearches';
import { useRouter } from 'next/router';
import usePopularSearches from '@api/hooks/search/usePopularSearches';
import useAutoCompleteResults from '@api/hooks/search/useAutoCompleteResults';
import Dynamic from '@base/Dynamic/Dynamic';
import uuid from '@utils/uuid';
import { setCookie } from '@utils/cookies';
import { segment } from '@actions/analytics';
import { SEARCH_CLICKED } from '@constants/segment';
import { evtNameCategoryMap } from '@constants/index';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { usePageInfo } from '@stores/AppCommonData';
import { getCurrentPageName } from '@stores/selectors/common';

const TrendingSearch = Dynamic(() =>
  import('@cont/Search/NewSearchDroddown/TrendingSearch/TrendingSearch')
);
const RecentSearch = Dynamic(() =>
  import('@cont/Search/NewSearchDroddown/RecentSearch/RecentSearch')
);

const Container = styled.div`
  display: flex;
  z-index: 5;
`;

const StyledSearchWrapper = styled(Search)`
  flex: 1;
  margin-right: ${Spacings.SPACING_2B};
  .aquilla-combobox {
    > div > div {
      height: 40px;
    }
    svg {
      color: var(--color-text-secondary);
    }
  }
`;

const StyledSearchListWrapper = styled(SearchList)`
  left: unset !important;
  height: unset;
  margin-top: ${Spacings.SPACING_0B};
  border-radius: 0 0 ${Spacings.SPACING_2B} ${Spacings.SPACING_2B};
  border: none;
  z-index: 5;
`;

const NewSearchTab = () => {
  const [value, setValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const {
    query: { goalUID }
  } = useRouter();
  const goalSegmentData = useGoalEventProps({ goalUID });
  const currentPageName = usePageInfo(getCurrentPageName);
  const [fetchRecentSearches, setFetchRecentSearches] = useState(false);
  const [autoCompleteQueryToFetchWith, setAutoCompleteQueryToFetchWith] =
    useState('');
  const debouncingRef = useRef(null);
  const { recentSearches } = useRecentSearches(goalUID, fetchRecentSearches);
  const { popularSearches } = usePopularSearches();
  const showRecentSearches = useMemo(
    () => isLoggedIn && !!recentSearches?.length,
    [isLoggedIn, recentSearches?.length]
  );
  const popularSearch = useMemo(
    () =>
      showRecentSearches
        ? recentSearches
        : popularSearches?.slice?.(0, 10) || [],
    [showRecentSearches, recentSearches, popularSearches]
  );
  const changeHandler = (autoCompleteQuery) => {
    if (debouncingRef.current) clearTimeout(debouncingRef.current);
    debouncingRef.current = setTimeout(
      () => setAutoCompleteQueryToFetchWith(autoCompleteQuery),
      300
    );
  };

  const { autoCompleteResults } = useAutoCompleteResults(
    autoCompleteQueryToFetchWith
  );

  const searchResults = value ? autoCompleteResults : popularSearch;
  const SearchDropdown =
    !value && showRecentSearches ? RecentSearch : TrendingSearch;

  useEffect(() => {
    if (showDropdown) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'auto';
  }, [showDropdown]);

  const handleOnChange = (changedValue) => {
    setValue(changedValue);
    changeHandler(changedValue);
  };

  const handleOnClear = () => {
    setValue('');
  };

  const handleOpenSearch = () => {
    const tpID = uuid();
    const twoMinutesFromNow = new Date(new Date().getTime() + 120 * 1000);
    setCookie('tapID', tpID, twoMinutesFromNow); // Delete cookie in 2 minutes.
    segment.track(
      SEARCH_CLICKED,
      {
        ...goalSegmentData,
        tap_id: tpID,
        search_section: 'Global',
        version: 2,
        last_primary_source: currentPageName
      },
      evtNameCategoryMap.WITHOUT_CATEGORY
    );
    setShowDropdown(true);
    setFetchRecentSearches(true);
  };

  const handleCloseSearch = () => {
    setShowDropdown(false);
  };

  return (
    <Container>
      <StyledSearchWrapper
        onOpen={handleOpenSearch}
        onClose={handleCloseSearch}
      >
        <SearchInput
          value={value}
          onChange={handleOnChange}
          onClear={handleOnClear}
          placeholder="Search courses, test series and educators"
          className="seachInput"
        />
        <StyledSearchListWrapper>
          <SearchDropdown popularSearches={searchResults} />
        </StyledSearchListWrapper>
      </StyledSearchWrapper>
    </Container>
  );
};

export default NewSearchTab;
